/**
 * レセプト内容一覧 — result table + pagination.
 * Columns: 受診者番号, 受診者名, 保険者番号, 保険者名, 施術年月
 * Row selection is handled by Ant Design's built-in `rowSelection` (checkbox only, no header label).
 */

import React, { useMemo, useRef } from 'react';
import dayjs from 'dayjs';
import type { ColumnsType } from 'antd/es/table';

import { AppPagination, AppTable, AppTooltip } from '@/components/atoms';
import { PatientEditLink } from '@/components/molecules';
import { convGtJMonth } from '@/utils/dateConverter';
import type { RezeptHistoriesListItem, RezeptHistoriesPagination } from '../types';

interface Props {
  data: RezeptHistoriesListItem[];
  pagination: RezeptHistoriesPagination | null;
  selectedIds: number[];
  onSelectChange: (ids: number[]) => void;
  onPageChange: (page: number) => void;
  isLoading: boolean;
  /** Whether a search has been run (controls AppTable empty-state text). */
  hasFilters: boolean;
  /**
   * Called when user toggles the header 全選択 checkbox.
   * `selected=true`  → caller should fetch all matching IDs across pages and set them.
   * `selected=false` → caller should clear selection across all pages.
   */
  onSelectAll?: (selected: boolean) => void;
}

/** Format YYYYMM integer → Japanese era month (e.g. 令和06年04月). */
function formatTreatmentYearMonth(ym: number | null | undefined): string {
  if (!ym) return '';
  const s = String(ym);
  if (s.length !== 6) return s;
  const iso = `${s.substring(0, 4)}-${s.substring(4, 6)}-01`;
  const d = dayjs(iso);
  return d.isValid() ? convGtJMonth(d) : s;
}

const RezeptHistoriesTable: React.FC<Props> = ({
  data,
  pagination,
  selectedIds,
  onSelectChange,
  onPageChange,
  isLoading,
  hasFilters,
  onSelectAll,
}) => {
  // Ant Design fires onChange AFTER onSelectAll. When user unticks the header,
  // onChange would re-compute keys as `previous - currentPage = others` and
  // restore other-page selections (due to preserveSelectedRowKeys). Set this
  // flag in onSelectAll(false) so the following onChange is ignored — letting
  // the parent's "clear all" decision stand.
  const skipNextOnChangeRef = useRef(false);

  const columns: ColumnsType<RezeptHistoriesListItem> = useMemo(
    () => [
      {
        title: <span className="cursor-text">受診者番号</span>,
        dataIndex: 'serial_number',
        key: 'serial_number',
        width: 180,
        render: (value: string, record) => (
          <PatientEditLink patientId={record.patient_id} value={value} ariaLabel="受診者情報" />
        ),
      },
      {
        title: <span className="cursor-text">受診者名</span>,
        dataIndex: 'patient_name',
        key: 'patient_name',
        render: (value: string) => (
          <AppTooltip title={value} showOnlyOnOverflow>
            <div className="break-all line-clamp-2 cursor-text">{value}</div>
          </AppTooltip>
        ),
      },
      {
        title: <span className="cursor-text">保険者番号</span>,
        dataIndex: 'insurer_number',
        key: 'insurer_number',
        width: 160,
        render: (value: string) => <span className="cursor-text">{value}</span>,
      },
      {
        title: <span className="cursor-text">保険者名</span>,
        dataIndex: 'insurer_name_org',
        key: 'insurer_name_org',
        render: (value: string) => (
          <AppTooltip title={value} showOnlyOnOverflow>
            <div className="break-all line-clamp-2 cursor-text">{value}</div>
          </AppTooltip>
        ),
      },
      {
        title: <span className="cursor-text">施術年月</span>,
        dataIndex: 'treatement_yearmonth',
        key: 'treatement_yearmonth',
        width: 180,
        render: (value: number) => (
          <span className="cursor-text">{formatTreatmentYearMonth(value)}</span>
        ),
      },
    ],
    []
  );

  return (
    <>
      <AppTable<RezeptHistoriesListItem>
        rowKey="id"
        columns={columns}
        dataSource={data}
        loading={isLoading}
        pagination={false}
        scroll={{ x: 'max-content' }}
        tableLayout="fixed"
        hasFilters={hasFilters}
        rowSelection={{
          selectedRowKeys: selectedIds,
          onChange: keys => {
            if (skipNextOnChangeRef.current) {
              skipNextOnChangeRef.current = false;
              return;
            }
            onSelectChange(keys.map(k => Number(k)));
          },
          columnWidth: 56,
          align: 'center',
          // Keep selections made on earlier pages when navigating to a new page.
          preserveSelectedRowKeys: true,
          // 全選択 header checkbox: tick/untick across ALL pages (BD FUNC_A.15_1/2).
          // Untick path: set skip flag so onChange can't restore other-page picks.
          onSelectAll: onSelectAll
            ? selected => {
                if (!selected) skipNextOnChangeRef.current = true;
                onSelectAll(selected);
              }
            : undefined,
        }}
      />

      <div className="flex justify-center mt-4">
        <AppPagination
          current={pagination?.current_page}
          total={pagination?.total}
          pageSize={pagination?.per_page}
          onChange={onPageChange}
        />
      </div>
    </>
  );
};

export default RezeptHistoriesTable;
